const breakpoints = [ '40em', '52em', '64em', '72em' ];

const mainFonts = `"Junicode-RegularCondensed","NHaasGrotesk", serif, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;`;

const fonts = {
	heading: mainFonts,
	body: mainFonts,
	monospace: 'Menlo, monospace',
	sans:
		'"NHaasGrotesk", sans-serif,-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"'
};

const fontSizes = {
	fs64: '4.5rem',
	fs3: '3rem',
	fs32: '3.2rem',
	fs24: '2.4rem',
	fs16: '1.6rem',
	fs12: '1.2rem',
	fs1: '1rem',
	fsmin: '.8rem'
};

export const fontWeights = {
	body: 400,
	heading: 700,
	bold: 700
};

const lineHeights = {
	heading: 1.4,
	body: 1.3
};

const colors = {
	black: {
		100: '#000000',
		90: '#1A1A1A',
		80: '#333333',
		70: '#4D4D4D',
		60: '#666666',
		50: '#7F7F7F',
		40: '#999999',
		30: '#B3B3B3',
		20: '#CCCCCC',
		10: '#E6E6E6'
	},
	white: {
		100: '#FFFFFF'
	},
	accent: '#C3F202',
	green: '#69db7c',
	red: '#fa5252',
	transparent: 'transparent',
	blue: '#4844FF'
};

const space = {
	xxxxs: 0,
	xxs: 4,
	xs: 8,
	s: 16,
	m: 24,
	l: 32,
	xl: 48,
	xxl: 64,
	xxxl: 96,
	xxxxl: 128
};

export const radii = {
	m: 10,
	xl: 20
};

export const images = {
	avatar: {
		width: 48,
		height: 48,
		borderRadius: 99999
	}
};

export const shadows = {
	button: {
		primary: {
			default: `0 0 0 1px ${colors.black[70]}`
		}
	}
};

export const easing = {
	bouncy: {
		string: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
		array: [ 0.68, -0.55, 0.265, 1.55 ]
	},
	smooth: {
		string: 'cubic-bezier(0.23, 1, 0.32, 1)',
		array: [ 0.23, 1, 0.32, 1 ]
	}
};

export const transitions = {
	smooth: {
		fast: `all 300ms ${easing.smooth.string}`,
		medium: `all 600ms ${easing.smooth.string}`,
		slow: `all 900ms ${easing.smooth.string}`
	}
};

export const styles = {
	root: {
		bg: 'black.100'
	},
	h3: {
		fontWeight: 'body'
	}
};

const boxes = {
	global: {
		color: 'white.100',
		fontFamily: 'body',
		WebkitFontSmoothing: 'antialiased',
		MozOsxFontSmoothing: 'grayscale',
		lineHeight: 1.3,
		fontSize: 'fs16'
	},
	column: {
		flexDirection: 'column'
	}
};

const buttons = {
	primary: {
		outline: 'none',
		bg: 'transparent',
		border: 'solid 1px',
		borderColor: 'white.100',
		borderRadius: 0,
		color: 'white.100',
		fontFamily: 'sans',
		fontSize: 'fs1',
		textTransform: 'uppercase',
		transition: transitions.smooth.fast,
		cursor: 'pointer',
		'@media (hover: hover)': {
			'&:hover': {
				bg: 'transparent',
				borderColor: 'accent',
				color: 'accent'
			},
			':disabled:hover': {
				bg: 'transparent',
				color: 'white.100'
			}
		},

		':disabled': {
			opacity: 0.5,
			cursor: 'not-allowed',
			textDecoration: 'line-through'
		}
	},

	secondary: {
		outline: 'none',
		bg: 'transparent',
		border: 'solid 1px',
		borderColor: 'black.100',
		borderRadius: 0,
		color: 'white.100',
		fontFamily: 'sans',
		fontSize: 'fsmin',
		opacity:.6,
		textTransform: 'uppercase',
		transition: transitions.smooth.fast,
		cursor: 'pointer',
		'@media (hover: hover)': {
			'&:hover': {
				bg: 'black.100',
				borderColor: 'black.100',
				opacity:.4
			}
		}
	}
};

const text = {
	vote: {
		xxs: {
			color: 'white.100',
			fontFamily: 'body',
			fontSize: 'fs1',
			lineHeight: 1.2
		},
		xs: {
			color: 'white.100',
			fontFamily: 'body',
			fontSize: 'fs12',
			fontWeight: 400,
			lineHeight: 1.4
		},
		s: {
			color: 'white.100',
			fontFamily: 'body',
			fontSize: 'fs16',
			fontWeight: 400,
			lineHeight: 1.3
			/* textTransform:"capitalize" */
		},
		m: {
			color: 'white.100',
			fontFamily: 'body',
			fontSize: 'fs24',
			fontWeight: 400,
			lineHeight: 1
		},

		l: {
			color: 'white.100',
			fontFamily: 'sans',
			fontSize: 'fs64',
			fontWeight: 400,
			lineHeight: 0.9,
			letterSpacing: '-.2888888889vw'
		},
		body: {
			fontSize: 'body',
			fontWeight: 400
		}
	}
};

const links = {
	default: {
		textDecoration: 'none',
		color: 'blue'
	},
	links: {
		fontSize: 'fs1',
		fontFamily: 'sans',
		color: '#fff',
		textDecoration: 'none',
		color: 'black.60',
		transition: transitions.smooth.fast,
		'@media (hover: hover)': {
			'&:hover': {
				color: 'white.100'
			}
		}
	}
};

// Construct the theme
const theme = {
	breakpoints,
	boxes,
	buttons,
	colors,
	fonts,
	fontSizes,
	fontWeights,
	lineHeights,
	radii,
	shadows,
	space,
	images,
	styles,
	text,
	transitions,
	links
};

export default theme;
